import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import withRollbarError from 'lib/client/withRollbarError'
import { some } from 'lodash'
import { useTranslation } from 'app/i18n/client'
import { useEffect } from 'react'
import { QUERY_ROOMS_PRICE, fetchRoomsPrice } from './fetchRoomsPrice'
import { RoomSelectInfo, RoomPriceModel } from 'core/bookings/types'
import useAuthentication from '../useAuthentication'
import useQueryMembershipDetail from '../useQueryMembershipDetail'
import { RESERVATION_MODE_ENUM } from 'utils/constants'

function useQueryRoomPrice(
  request: {
    hotelCode?: string
    couponCode?: string | null
    spendingPoint?: number | null
    checkin?: string
    checkout: string
    rooms?: RoomSelectInfo[]
    reservationNo?: string | null
    enable?: boolean
    mode?: RESERVATION_MODE_ENUM // For NFT we will use secret mode
  },
  onSuccess?: (data: any) => void,
  onError?: (error: unknown) => void,
): UseQueryResult<RoomPriceModel | null> {
  const {
    hotelCode,
    checkin,
    checkout,
    rooms,
    couponCode,
    spendingPoint,
    enable,
    reservationNo,
    mode,
  } = request

  const params = useParams<{ locale: string }>()
  const { locale } = params || {}

  const { withTriplaApiRequest, logout } = useAuthentication()
  const { isFetching } = useQueryMembershipDetail()

  const { t } = useTranslation()

  const result = useQuery({
    queryKey: [
      QUERY_ROOMS_PRICE,
      {
        hotelCode,
        checkin,
        checkout,
        rooms,
        couponCode: couponCode || '',
        spendingPoint: spendingPoint,
        reservationNo: reservationNo || null,
        locale: locale as string,
        mode,
      },
    ],
    queryFn: withTriplaApiRequest(fetchRoomsPrice),
    enabled:
      !!rooms &&
      !!checkin &&
      !!checkout &&
      !!hotelCode &&
      !!enable &&
      !isFetching,
  })

  const { data, isSuccess, isError, error } = result

  useEffect(() => {
    if (isError && error) {
      if (
        (error as any)?.response?.status === 400 &&
        some(
          (error as any)?.response?.data.errors,
          (e: any) =>
            e.title ===
            t(
              'Discounts offered by the hotel have been changed/removed right now. Please go back to the first screen and search for room type and plans again as new prices should be applied.',
            ),
        )
      ) {
        logout()
        return
      }

      withRollbarError(onError)(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error])

  useEffect(() => {
    if (isSuccess && data) onSuccess?.(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
  return result
}

export default useQueryRoomPrice
